<template>
    <div>
        <div class="filters-row row mb-3">
            <div class="col-md-3 col-12 mb-2">
                <input
                    v-model="filters.search"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Cari no. order / customer..."
                    @keyup.enter="applyFilters"
                />
            </div>
            <div class="col-md-2 col-6 mb-2">
                <select v-model="filters.status" class="form-control form-control-sm" @change="applyFilters">
                    <option value="">Semua Status</option>
                    <option value="pending">Pending</option>
                    <option value="processed">Diproses</option>
                    <option value="shipped">Dikirim</option>
                    <option value="completed">Selesai</option>
                    <option value="cancelled">Dibatalkan</option>
                </select>
            </div>
            <div class="col-md-2 col-6 mb-2">
                <select v-model="filters.payment_status" class="form-control form-control-sm" @change="applyFilters">
                    <option value="">Semua Pembayaran</option>
                    <option value="pending">Belum Bayar</option>
                    <option value="paid">Lunas</option>
                    <option value="expired">Expired</option>
                    <option value="failed">Gagal</option>
                    <option value="refund">Refund</option>
                </select>
            </div>
            <div class="col-md-2 col-6 mb-2">
                <input v-model="filters.from" type="date" class="form-control form-control-sm" @change="applyFilters" />
            </div>
            <div class="col-md-2 col-6 mb-2">
                <input v-model="filters.to" type="date" class="form-control form-control-sm" @change="applyFilters" />
            </div>
            <div class="col-md-1 col-12 mb-2">
                <button class="btn btn-primary btn-sm btn-block" @click="applyFilters">
                    <i class="fas fa-search"></i>
                </button>
            </div>
        </div>

        <div v-if="loading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ml-2 text-muted">Memuat order...</span>
        </div>

        <div v-else class="table-responsive">
            <table class="table table-hover mb-0">
                <thead>
                    <tr>
                        <th>No. Order</th>
                        <th>Tanggal</th>
                        <th>Customer</th>
                        <th class="text-right">Total</th>
                        <th>Status</th>
                        <th>Pembayaran</th>
                        <th class="text-center">Aksi</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="order in orders" :key="order.id">
                        <td>
                            <code class="small">{{ shortRef(order.order_id) }}</code>
                        </td>
                        <td><small>{{ formatTime(order.created_at) }}</small></td>
                        <td>
                            <div class="font-weight-medium">{{ order.customer_name }}</div>
                            <small class="text-muted">{{ order.customer_phone }}</small>
                        </td>
                        <td class="text-right">
                            <div>{{ formatRupiah(order.total_amount) }}</div>
                            <small v-if="order.shop_owner_amount" class="text-success">Net {{ formatRupiah(order.shop_owner_amount) }}</small>
                        </td>
                        <td><span :class="statusBadge(order.status)">{{ statusLabel(order.status) }}</span></td>
                        <td><span :class="paymentBadge(order.payment_status)">{{ paymentLabel(order.payment_status) }}</span></td>
                        <td class="text-center text-nowrap">
                            <button class="btn btn-sm btn-outline-primary mr-1" @click="viewDetail(order.id)" title="Detail">
                                <i class="fas fa-eye"></i>
                            </button>
                            <button
                                v-if="order.payment_status === 'pending'"
                                class="btn btn-sm btn-success"
                                @click="quickMarkPaid(order)"
                                title="Konfirmasi Bayar"
                            >
                                <i class="fas fa-check"></i>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="orders.length === 0">
                        <td colspan="7" class="text-center py-5">
                            <i class="fas fa-shopping-cart fa-2x text-muted mb-2 d-block"></i>
                            <span class="text-muted">Belum ada order{{ hasFilters ? ' dengan filter ini' : '' }}.</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="totalRecords > limit" class="d-flex justify-content-between align-items-center mt-3">
            <small class="text-muted">{{ totalRecords }} order total</small>
            <div>
                <button class="btn btn-sm btn-light" :disabled="page <= 1" @click="changePage(page - 1)">
                    <i class="fas fa-chevron-left"></i>
                </button>
                <span class="mx-2 small">Hal {{ page }}</span>
                <button class="btn btn-sm btn-light" :disabled="page * limit >= totalRecords" @click="changePage(page + 1)">
                    <i class="fas fa-chevron-right"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'OrderTable',
    props: {
        shopId: { type: Number, required: true },
    },
    data() {
        return {
            orders: [],
            loading: false,
            page: 1,
            limit: 20,
            totalRecords: 0,
            filters: {
                search: '',
                status: '',
                payment_status: '',
                from: '',
                to: '',
            },
        };
    },
    computed: {
        hasFilters() {
            return Object.values(this.filters).some(v => v);
        },
    },
    mounted() {
        this.loadOrders();
        this.$root.$on('refresh-order-table', this.loadOrders);
    },
    beforeDestroy() {
        this.$root.$off('refresh-order-table', this.loadOrders);
    },
    methods: {
        async loadOrders() {
            this.loading = true;
            this.$emit('loading', true);
            try {
                const params = {
                    limit: this.limit,
                    page: this.page,
                    ...this.filters,
                };
                Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
                const response = await this.$store.dispatch('shop_order/shop_order_list', {
                    shopId: this.shopId,
                    params,
                });
                this.orders = response.data.data.orders || [];
                this.totalRecords = response.data.data.total_records || 0;
            } catch (error) {
                console.error('Error loading orders:', error);
                this.orders = [];
                this.$toast?.error?.('Gagal memuat daftar order');
            } finally {
                this.loading = false;
                this.$emit('loading', false);
            }
        },
        applyFilters() {
            this.page = 1;
            this.loadOrders();
        },
        changePage(p) {
            this.page = p;
            this.loadOrders();
        },
        viewDetail(orderId) {
            this.$emit('view-detail', orderId);
        },
        async quickMarkPaid(order) {
            if (!confirm(`Konfirmasi pembayaran order ${this.shortRef(order.order_id)}?`)) return;
            try {
                await this.$store.dispatch('shop_order/shop_order_mark_paid', order.id);
                this.$toast.success('Pembayaran dikonfirmasi');
                this.loadOrders();
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal konfirmasi');
            }
        },
        shortRef(ref) {
            if (!ref) return '-';
            return ref.length > 16 ? ref.slice(0, 8) + '...' + ref.slice(-4) : ref;
        },
        formatRupiah(v) {
            return 'Rp ' + (v || 0).toLocaleString('id-ID');
        },
        formatTime(t) {
            if (!t) return '-';
            const ms = typeof t === 'number' || /^\d+$/.test(String(t)) ? parseInt(t) : t;
            return moment(ms).format('DD MMM YYYY HH:mm');
        },
        statusLabel(s) {
            return { pending: 'Pending', processed: 'Diproses', processing: 'Diproses', shipped: 'Dikirim', completed: 'Selesai', cancelled: 'Dibatalkan' }[s] || s;
        },
        paymentLabel(s) {
            return { pending: 'Belum Bayar', paid: 'Lunas', expired: 'Expired', failed: 'Gagal', refund: 'Refund' }[s] || s;
        },
        statusBadge(s) {
            return ['badge', {
                'badge-warning': s === 'pending',
                'badge-info': s === 'processed' || s === 'processing',
                'badge-primary': s === 'shipped',
                'badge-success': s === 'completed',
                'badge-danger': s === 'cancelled',
                'badge-secondary': !['pending','processed','processing','shipped','completed','cancelled'].includes(s),
            }];
        },
        paymentBadge(s) {
            return ['badge', {
                'badge-warning': s === 'pending',
                'badge-success': s === 'paid',
                'badge-danger': s === 'failed' || s === 'expired',
                'badge-secondary': s === 'refund',
            }];
        },
    },
};
</script>

<style scoped>
.font-weight-medium { font-weight: 500; }
</style>
